import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, ImagePlus, Loader2, X, FileText, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const isImage = (att) => (att?.content_type || '').startsWith('image/');
export const attachmentUrl = (ticketId, att) => `${API_URL}/api/support/tickets/${ticketId}/attachments/${att.id}`;

/** Upload files one at a time to a ticket; returns the attachment docs. */
export async function uploadTicketFiles(ticketId, files) {
  const out = [];
  for (const f of files) {
    if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15 MB`); continue; }
    const form = new FormData();
    form.append('file', f);
    const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/attachments`, { method: 'POST', credentials: 'include', body: form });
    if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || `Upload failed for ${f.name}`);
    out.push(await res.json());
  }
  return out;
}

/** Full-screen viewer with prev/next; click outside or Esc to close. */
export function ImageLightbox({ ticketId, images, index, onClose, onIndex }) {
  useEffect(() => {
    if (index == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') onIndex((index + 1) % images.length); if (e.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onClose, onIndex]);
  if (index == null || !images[index]) return null;
  const att = images[index];
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="ticket-lightbox">
      <button type="button" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Close" data-testid="ticket-lightbox-close"><X className="w-5 h-5" /></button>
      <a href={attachmentUrl(ticketId, att)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs text-white/80 hover:text-white bg-white/10 rounded-full px-3 py-1.5" data-testid="ticket-lightbox-open"><ExternalLink className="w-3.5 h-3.5" /> Open original</a>
      {images.length > 1 && <>
        <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + images.length) % images.length); }} className="absolute left-2 sm:left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Previous" data-testid="ticket-lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % images.length); }} className="absolute right-2 sm:right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Next" data-testid="ticket-lightbox-next"><ChevronRight className="w-6 h-6" /></button>
      </>}
      <img src={attachmentUrl(ticketId, att)} alt={att.filename} onClick={(e) => e.stopPropagation()} className="max-w-[94vw] max-h-[88vh] object-contain rounded shadow-2xl" data-testid="ticket-lightbox-img" />
      <div className="absolute bottom-3 text-xs text-white/70">{att.filename}{images.length > 1 ? ` · ${index + 1} / ${images.length}` : ''}</div>
    </div>
  );
}

/** Thumbnails for image attachments (tap to view full size), plain links for everything else. */
export function AttachmentGallery({ ticketId, attachments, size = 'md', testPrefix = 'ticket-att' }) {
  const [view, setView] = useState(null);
  if (!attachments?.length) return null;
  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));
  const dim = size === 'xs' ? 'w-14 h-14' : size === 'sm' ? 'w-20 h-20' : 'w-32 h-32';
  return (
    <div className="space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((att, i) => (
            <button type="button" key={att.id} onClick={(e) => { e.stopPropagation(); setView(i); }} title={att.filename} className={`${dim} rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-slate-400`} data-testid={`${testPrefix}-${att.id}`}>
              <img src={attachmentUrl(ticketId, att)} alt={att.filename} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((att) => (
            <li key={att.id}>
              <a href={attachmentUrl(ticketId, att)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline" data-testid={`${testPrefix}-${att.id}`}>
                <FileText className="w-4 h-4" /> {att.filename} <span className="text-xs text-slate-400">({((att.size || 0) / 1024).toFixed(0)} KB)</span>
              </a>
            </li>
          ))}
        </ul>
      )}
      <ImageLightbox ticketId={ticketId} images={images} index={view} onClose={() => setView(null)} onIndex={setView} />
    </div>
  );
}

/** "Add photos or files" button that uploads straight onto the ticket. */
export function AttachmentUploader({ ticketId, onUploaded, label = 'Add photos or files', testid = 'ticket-upload-btn' }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try { const atts = await uploadTicketFiles(ticketId, files); onUploaded(atts); toast.success(`${atts.length} file${atts.length === 1 ? '' : 's'} added`); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50" data-testid={testid}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {busy ? 'Uploading…' : label}
      </button>
    </>
  );
}

/** Picks images for a note; shows local previews until the note is submitted. */
export function NoteImagePicker({ files, setFiles, testid = 'ticket-note-images' }) {
  const ref = useRef(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={ref} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ''; }} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid={testid}><ImagePlus className="w-4 h-4" /> Attach images</button>
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="relative w-12 h-12 rounded border border-slate-200 overflow-hidden" data-testid={`${testid}-preview-${i}`}>
          <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
          <button type="button" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="absolute -top-0.5 -right-0.5 bg-white/90 rounded-full p-0.5 text-slate-600 hover:text-rose-600" aria-label="Remove"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
}
